import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import type { SxProps } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { NumberFormat } from "@/components/NumberFormat";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { TRANSACTION_TABLE_COLUMN_COUNT } from "@/pages/billing/v2/tabs/transactionHistory/utils";

const styles = {
  cell: {
    paddingTop: 0,
    paddingBottom: 0,
    borderBottom: "none",
  },
  content: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "16px 32px",
    padding: "16px 24px",
    backgroundColor: theme.colors.neutral010,
    borderRadius: "4px",
    marginBottom: "8px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  label: {
    color: theme.colors.neutral050,
    fontSize: "0.75rem",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface TransactionExpandedRowProps {
  item: InterludeTypes["BalanceTransactionItem"];
  open: boolean;
}

export const TransactionExpandedRow: React.FC<TransactionExpandedRowProps> =
  function TransactionExpandedRow({ item, open }) {
    const { t } = useTranslation();

    const settlement = item.settlementItem;
    if (!settlement) return null;

    return (
      <TableRow>
        <TableCell sx={styles.cell} colSpan={TRANSACTION_TABLE_COLUMN_COUNT}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={styles.content}>
              <Box sx={styles.field}>
                <Box component="span" sx={styles.label}>
                  {t("billing.transaction.detail.quantity")}
                </Box>
                <Typography variant="body">
                  <NumberFormat value={settlement.quantity} />
                </Typography>
              </Box>
              <Box sx={styles.field}>
                <Box component="span" sx={styles.label}>
                  {t("billing.transaction.detail.unitPrice")}
                </Box>
                <Typography variant="body">
                  <NumberFormat
                    numberFormatPreset="currency"
                    numberFormatOptions={{ currency: item.currency }}
                    value={settlement.unitPrice}
                  />
                </Typography>
              </Box>
              <Box sx={styles.field}>
                <Box component="span" sx={styles.label}>
                  {t("billing.transaction.detail.totalPrice")}
                </Box>
                <Typography variant="body">
                  <NumberFormat
                    numberFormatPreset="currency"
                    numberFormatOptions={{ currency: item.currency }}
                    value={settlement.totalPrice}
                  />
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };
